/**
 * spin-blade — 旋刃近戰。動詞:**衝進敵群用身體碰**。
 *
 * 玩法:坦克周圍環繞旋轉刀刃,**近距離接觸判定**對環帶內敵人造成持續傷害,
 * **無子彈**。沒有遠程輸出,要主動開進敵群裡刮。配合會貼上來的 'chase' 敵人
 * (老闆 2026-06-06「可以有追逐主角的敵人,這樣近戰才有用」)最對味。
 *
 * 設計:每幀以固定角速度旋轉刀刃角度(視覺,scene 用 setBladeAngle 轉 sprite);
 * 傷害用 types.ts 的 bladeHitIndices(環帶命中,純函式)每 tickInterval 施一次
 * 持續傷害(不是每幀,避免高 fps 爆傷)。
 * 鐵則:威力 = baseBulletDamage × 倍率 × 等級曲線,不讀修煉值。
 */

import type { WeaponBehavior, WeaponTickContext } from './types';
import {
  bladeHitIndices,
  weaponLevelDamageMult,
  applyRateMod,
  spreadWidthMult,
  sizeRangeMult,
  blastRangeBonus,
  extraHitChains,
} from './types';

export const SPIN_BLADE_ID = 'spin-blade';
export const SPIN_BLADE_BLURB = '身邊轉刀刃,開進敵群就持續刮血(近戰、沒子彈)。';

/**
 * 刀刃環繞半徑(px):敵人中心離玩家多近會被刮到。
 * boss 2026-06-14 試玩「旋刃太弱、距離太短、太容易死」:近戰要貼臉=被圍死,光高傷沒用。
 * 主修正 = **大幅加射程**(56→95),讓玩家從敵群外圈刮、不必鑽進去 → 距離夠 + 少被圍。
 * 射程(orbit/band)不在 DPS 收斂模型內,加大不影響「範圍×傷害」平衡,只提升「打得到/活得久」。
 */
export const SPIN_BLADE_ORBIT_RADIUS = 95;
/** 命中環帶半寬(px):[radius-band, radius+band] 內都算接觸(K-3 寬容)。22→36(環帶更厚,更好刮到)。 */
export const SPIN_BLADE_BAND = 36;
/** 旋轉角速度(弧度/sec):純視覺,刀刃轉得明顯。 */
export const SPIN_BLADE_SPIN_RAD_PER_SEC = 7;
/**
 * 持續傷害的施加間隔(ms):每隻敵人每這麼久吃一次刮傷。
 * v2「算範圍×傷害」拉到 260 收斂。spin 是 range★1(近戰最高風險)=全武器最高單體 DPS,
 * 已頂到「強 build tier1 TTK ≥ 地板(不秒王)」極限,**傷害不可再加**(加了強 build 又秒王)。
 * boss 試玩「太弱」的真因是「貼不到/活不久=用不出來」,故只加射程不加傷(見 orbit/band)。
 */
export const SPIN_BLADE_TICK_INTERVAL_MS = 260;
/** 每次刮傷倍率:近戰要主動貼=最高風險,單發已是全武器最高(受 TTK 地板封頂,不再加)。 */
export const SPIN_BLADE_DAMAGE_MULT = 1.0;
/** 「穿透/彈跳」詮釋:每一層額外刮環帶再加寬的 px(刮到更廣一圈)。 */
export const SPIN_BLADE_REACH_PER_CHAIN = 14;

export class SpinBladeBehavior implements WeaponBehavior {
  readonly id = SPIN_BLADE_ID;
  private _angleRad = 0;
  private _lastTickMs = -1;

  tick(ctx: WeaponTickContext): void {
    // 三選一詮釋 — spin-blade 版:
    //   攻擊增速 → 旋轉更快(視覺)+ 刮傷 tick 更密(tickInterval × mult = 每秒刮更多次)。
    //   多顆子彈 → 多一片旋刃 = 命中環帶加寬(band ×(1+0.25n))。
    //   巨彈 → 刃更大(orbit 半徑 + band ×bulletSizeMult)。
    //   爆裂/穿透/彈跳 → 刮到更廣一圈(band + 額外 reach)。
    const m = ctx.mods;
    const effAuto = m.effectiveAutoFireMult();

    // 視覺:旋轉刀刃角度(攻擊增速也轉更快;scene 轉 sprite;headless 缺 seam 時略過)。
    const spinPerSec = SPIN_BLADE_SPIN_RAD_PER_SEC / Math.max(0.4, effAuto);
    this._angleRad = (this._angleRad + spinPerSec * ctx.dt) % (Math.PI * 2);
    ctx.setBladeAngle?.(this._angleRad);

    // 傷害:每 tickInterval 施一次持續刮傷(不是每幀,避免 fps 影響 DPS)。
    // 攻擊增速縮短間隔 → 每秒刮更多次(DPS 提升,符合「更快」)。
    const tickIntervalMs = applyRateMod(SPIN_BLADE_TICK_INTERVAL_MS, effAuto);
    if (this._lastTickMs >= 0 && ctx.nowMs - this._lastTickMs < tickIntervalMs) {
      return;
    }
    this._lastTickMs = ctx.nowMs;

    // 環帶判定:多片旋刃(散彈)加寬 band × 巨彈倍率 + 爆裂帶寬 + 穿透/彈跳額外 reach。
    const band =
      SPIN_BLADE_BAND
        * spreadWidthMult(m.effectiveSpreadBullets())
        * sizeRangeMult(m.bulletSizeMult)
      + blastRangeBonus(m.spreadBlastRadius, 0.5)
      + extraHitChains(m.pierceCount, m.bounceCount + m.evoBounceBonus) * SPIN_BLADE_REACH_PER_CHAIN;
    // 巨彈也讓刃環整體變大(orbit 半徑放大,刮到更外圈)。
    const orbit = SPIN_BLADE_ORBIT_RADIUS * sizeRangeMult(m.bulletSizeMult);

    const targets = ctx.enemies();
    const hits = bladeHitIndices(
      { x: ctx.playerX, y: ctx.playerY },
      targets,
      orbit,
      band,
    );
    if (hits.length === 0) return;
    const dmg = Math.round(
      ctx.baseBulletDamage() * SPIN_BLADE_DAMAGE_MULT * weaponLevelDamageMult(ctx.weaponLevel),
    );
    for (const i of hits) {
      const t = targets[i];
      if (t && t.alive) t.takeDamage(dmg);
    }
  }

  reset(): void {
    this._angleRad = 0;
    this._lastTickMs = -1;
  }

  /** 測試/UI 用:目前刀刃角度(弧度)。 */
  angle(): number {
    return this._angleRad;
  }
}
